import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
} from '@angular/forms';
import {
  DateInputsModule,
  SelectionRange,
} from '@progress/kendo-angular-dateinputs';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { buildYears } from './build-years';
import {
  computeRelativeRangePreset,
  RELATIVE_RANGE_PRESET_LABELS,
  RELATIVE_RANGE_PRESETS,
  resolveDateRangePickerTemplate,
  type UpsDateRangePickerVariant,
  type UpsRelativeRangePreset,
} from './relative-date-range-presets.utils';

export type UpsPeriodPreset = 'CUSTOM' | 'YTD' | 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface UpsDateRangeSelection {
  start: Date | null;
  end: Date | null;
  preset: UpsPeriodPreset;
  year: number;
  relativePreset?: UpsRelativeRangePreset | null;
}

export type RelativeDatePickerView = 'presets' | 'custom';

@Component({
  selector: 'ups-date-range-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    DateInputsModule,
    DropDownListModule,
    ButtonsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UpsDateRangePickerComponent),
      multi: true,
    },
  ],
})
export class UpsDateRangePickerComponent implements ControlValueAccessor {
  /** Optional minimum date constraint */
  readonly min = input<Date | undefined>(undefined);

  /** Optional maximum date constraint */
  readonly max = input<Date | undefined>(undefined);

  /** Optional override for the years dropdown list */
  readonly years = input<number[] | undefined>(undefined);

  /** Whether to show the Clear button (default: true) */
  readonly showClear = input(true);

  /** Whether the entire component is disabled (default: false) */
  readonly disabled = input(false);

  /**
   * Display variant. Empty/unset keeps the legacy inline picker (YTD/Q1…).
   * `relative-presets` renders rolling windows + custom calendar (#31506).
   */
  readonly variant = input<UpsDateRangePickerVariant | undefined>(undefined);

  /** Emits the current selection whenever it changes */
  readonly valueChange = output<UpsDateRangeSelection>();

  readonly templateMode = computed(() =>
    resolveDateRangePickerTemplate(this.variant())
  );

  readonly relativeRangePresets = RELATIVE_RANGE_PRESETS;
  readonly relativeRangePresetLabels = RELATIVE_RANGE_PRESET_LABELS;

  // Internal state
  readonly range = signal<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });
  readonly selectedYear = signal(new Date().getFullYear());
  readonly preset = signal<UpsPeriodPreset>('CUSTOM');
  readonly relativePreset = signal<UpsRelativeRangePreset | null>(null);
  readonly relativePickerView = signal<RelativeDatePickerView>('presets');
  readonly draftRange = signal<SelectionRange>({ start: null, end: null });

  // CVA disabled state (merged with input)
  private readonly cvaDisabled = signal(false);
  readonly isDisabled = computed(() => this.disabled() || this.cvaDisabled());

  // Computed: years list for the dropdown
  readonly yearsInternal = computed(() => this.years() ?? this.buildYears());

  // Preset options for template iteration
  readonly presetOptions: Exclude<UpsPeriodPreset, 'CUSTOM'>[] = [
    'YTD',
    'Q1',
    'Q2',
    'Q3',
    'Q4',
  ];

  // CVA callbacks
  private onChange?: (value: UpsDateRangeSelection) => void;
  private onTouched?: () => void;

  // --- ControlValueAccessor ---

  writeValue(value: UpsDateRangeSelection | null): void {
    if (value) {
      this.selectedYear.set(value.year);
      this.preset.set(value.preset);
      this.range.set({ start: value.start, end: value.end });
      this.relativePreset.set(value.relativePreset ?? null);
    } else {
      this.selectedYear.set(new Date().getFullYear());
      this.preset.set('CUSTOM');
      this.range.set({ start: null, end: null });
      this.relativePreset.set(null);
    }
    this.relativePickerView.set('presets');
  }

  registerOnChange(fn: (value: UpsDateRangeSelection) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  // --- Default variant handlers ---

  onManualRangeChange(range: { start: Date | null; end: Date | null }): void {
    this.range.set({ start: range?.start ?? null, end: range?.end ?? null });
    this.preset.set('CUSTOM');
    this.onTouched?.();
    this.emitValue();
  }

  onYearChange(year: number): void {
    this.selectedYear.set(year);
    if (this.preset() !== 'CUSTOM') {
      this.range.set(
        this.computeRange(
          year,
          this.preset() as Exclude<UpsPeriodPreset, 'CUSTOM'>
        )
      );
    }
    this.emitValue();
  }

  applyPreset(p: Exclude<UpsPeriodPreset, 'CUSTOM'>): void {
    this.preset.set(p);
    this.range.set(this.computeRange(this.selectedYear(), p));
    this.emitValue();
  }

  clearPreset(): void {
    this.preset.set('CUSTOM');
    this.range.set({ start: null, end: null });
    this.relativePreset.set(null);
    this.onTouched?.();
    this.emitValue();
  }

  // --- relative-presets variant handlers ---

  isRelativePresetActive(
    p: Exclude<UpsRelativeRangePreset, 'CUSTOM'>
  ): boolean {
    return this.relativePreset() === p;
  }

  applyRelativePreset(p: Exclude<UpsRelativeRangePreset, 'CUSTOM'>): void {
    const next = computeRelativeRangePreset(p, new Date());
    this.range.set(next);
    this.relativePreset.set(p);
    this.relativePickerView.set('presets');
    this.onTouched?.();
    this.emitValue();
  }

  openRelativeCustom(): void {
    this.draftRange.set({
      start: this.range().start,
      end: this.range().end,
    });
    this.relativePickerView.set('custom');
  }

  onDraftRangeChange(range: SelectionRange): void {
    this.draftRange.set({
      start: range?.start ?? null,
      end: range?.end ?? null,
    });
  }

  cancelRelativeCustom(): void {
    this.relativePickerView.set('presets');
  }

  applyRelativeCustom(): void {
    const draft = this.draftRange();
    if (!draft.start || !draft.end) {
      return;
    }
    this.range.set({ start: draft.start, end: draft.end });
    this.relativePreset.set('CUSTOM');
    this.relativePickerView.set('presets');
    this.onTouched?.();
    this.emitValue();
  }

  // --- Helpers ---

  private computeRange(
    year: number,
    preset: Exclude<UpsPeriodPreset, 'CUSTOM'>
  ): { start: Date; end: Date } {
    const currentYear = new Date().getFullYear();

    if (preset === 'YTD') {
      const ytdStart = new Date(year, 0, 1);
      const ytdEnd =
        year < currentYear
          ? this.endOfDay(new Date(year, 11, 31))
          : this.endOfDay(new Date());
      return { start: ytdStart, end: ytdEnd };
    }

    const q = Number(preset.substring(1));
    const startMonth = (q - 1) * 3;
    const rangeStart = new Date(year, startMonth, 1);
    const rangeEnd = this.endOfDay(new Date(year, startMonth + 3, 0));
    return { start: rangeStart, end: rangeEnd };
  }

  private endOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }

  private buildYears(): number[] {
    return buildYears(new Date().getFullYear(), 7, 3);
  }

  private emitValue(): void {
    const value: UpsDateRangeSelection = {
      start: this.range().start,
      end: this.range().end,
      preset: this.preset(),
      year: this.selectedYear(),
      relativePreset:
        this.templateMode() === 'relative-presets'
          ? this.relativePreset()
          : null,
    };
    this.onChange?.(value);
    this.valueChange.emit(value);
  }
}
